/**
 * Quilty Voice System
 * 
 * Provides the distinctive Quilty voice for user-facing messages.
 * System/functional messages stay plain; user acknowledgements get personality.
 */

interface ToastMessages {
  [key: string]: string[] | string;
}

interface ToastCategories {
  auth: ToastMessages;
  upload: ToastMessages;
  analysis: ToastMessages;
  general: ToastMessages;
}

interface VoiceConfig {
  version: string;
  voice_variant: string;
  persona: {
    description: string;
    tone_principles: string[];
    reference_palette: string[];
  };
  strings: {
    preview: Record<string, string>;
    budget_descriptions: Record<string, string>;
    ip_callout: string;
    loading_states: Record<string, string>;
    error_states: Record<string, string>;
    toasts: ToastCategories;
  };
  system_messages: {
    description: string;
    examples: string[];
  };
}

// Voice config embedded directly to avoid SSR issues with JSON imports
const voiceConfig: VoiceConfig = {
  version: "2.0",
  voice_variant: "cinephile",
  persona: {
    description: "Quilty is a learned cinephile with the dry wit of Peter Sellers and the dark irony of Kubrick. It speaks to fellow film people - not condescending, but as one insider to another.",
    tone_principles: [
      "Dry, understated wit over punchlines",
      "Occasional film references that reward the knowledgeable",
      "Never sarcastic on errors - wry acknowledgement instead",
      "Concise - Quilty is clever, not chatty"
    ],
    reference_palette: [
      "Kubrick", "Sellers", "Wilder", "Sturges", "Lubitsch",
      "Truffaut", "Godard", "Kurosawa", "Hitchcock", "the Coens",
      "Welles", "Hawks", "Ford", "Lean", "Fellini"
    ]
  },
  strings: {
    preview: {
      title: "The Opening Gambit",
      subtitle: "Your screenplay's strategic position revealed",
      synopsis_section: "The Narrative Revealed",
      genres_section: "Genre DNA",
      budget_section: "Production Reality",
      territory_section: "Global Reach",
      agents_section: "Representation Matches",
      chain_section: "The Players",
      cta_title: "Ready for the deep dive?",
      cta_description: "Full analysis unlocks market positioning, talent matching, and production intelligence.",
      cta_button: "Run Full Analysis",
      cta_badge: "Complete Insights",
      locked_title: "Full Analysis Available",
      fallback_synopsis: "Synopsis available in full analysis..."
    },
    budget_descriptions: {
      micro: "Lean and agile — the Cassavetes approach",
      indie: "Independent spirit, Sundance territory",
      mid: "Studio-adjacent — room to maneuver",
      studio: "Major league production values",
      unknown: "Budget assessment pending"
    },
    ip_callout: "**Adaptation Potential** — {type} source material detected. Full analysis maps licensing landscape.",
    loading_states: {
      processing: "One moment — assembling the usual suspects...",
      analyzing: "Reading between the lines...",
      generating: "The analysis takes shape...",
      complete: "Fin. Analysis complete."
    },
    error_states: {
      timeout: "Connection interrupted. Please try again.",
      failure: "Something went wrong. Our team is on it.",
      network: "Network unavailable. Check your connection."
    },
    toasts: {
      auth: {
        login_success: [
          "Here's looking at you. Welcome back.",
          "I think this is the beginning of a beautiful session.",
          "You're in. As if there was ever any doubt.",
          "We're gonna need a bigger dashboard. Welcome back."
        ],
        login_success_recovery: [
          "After all, tomorrow is another day. And today you're in.",
          "I'm not even mad. That was impressive. Welcome back."
        ],
        login_error: [
          "You shall not pass. Check your credentials.",
          "These aren't the credentials you're looking for. Try again."
        ],
        login_error_escalated: [
          "Frankly, you might need a password reset at this point.",
          "Houston, we have a problem. Try 'Forgot Password.'"
        ],
        logout: [
          "I'll be back. — You, probably. See you soon.",
          "That'll do. Signed out successfully.",
          "So long, partner. Until next time."
        ],
        register_success: [
          "Welcome to the party, pal. Upload your first screenplay.",
          "You had us at 'Sign Up.' Let's get started."
        ],
        session_expired: "Session expired. You know the drill — log in again."
      },
      upload: {
        success: [
          "The stuff that dreams are made of. Screenplay received.",
          "Here's Johnny — your screenplay has arrived. Analysis starting.",
          "May the analysis be with you. Upload complete."
        ],
        error: [
          "Houston, we have a problem. Upload interrupted — try again.",
          "I've got a bad feeling about that file. Try once more."
        ],
        processing: "Hold on to your butts. Processing..."
      },
      analysis: {
        preview_complete: [
          "Elementary. Preview analysis ready.",
          "First impressions are in. Go ahead, make your day."
        ],
        full_started: [
          "Fasten your seatbelts. Full analysis underway.",
          "We'll make you an analysis you can't refuse. Typically under an hour."
        ],
        full_complete: [
          "Here's looking at your results. Full analysis complete.",
          "Rosebud found. All insights unlocked."
        ],
        error: [
          "Forget it, Jake — something went wrong. We're on it.",
          "There's been a glitch in the matrix. Please retry."
        ]
      },
      general: {
        success: "Consider it done.",
        error: "Something went wrong. Please try again.",
        info: "For the record:",
        warning: "Pay attention:"
      }
    }
  },
  system_messages: {
    description: "These messages stay plain and professional - no personality",
    examples: [
      "Session expired. Please log in again.",
      "Network error. Please check your connection.",
      "File format not supported. Please use PDF, DOCX, or TXT.",
      "Server temporarily unavailable. Please try again later."
    ]
  }
};

/**
 * Get a random item from an array, or return the string if it's not an array
 */
function getRandomOrSingle(messages: string[] | string): string {
  if (Array.isArray(messages)) {
    return messages[Math.floor(Math.random() * messages.length)];
  }
  return messages;
}

/**
 * Toast Voice Helper
 * 
 * Maps semantic keys to Quilty voice messages.
 * Use for user-facing acknowledgements (login, upload, analysis completion).
 * 
 * @param category - The message category (auth, upload, analysis, general)
 * @param key - The specific message key within the category
 * @param variables - Optional variables for interpolation
 * @returns The voice-aware message string
 * 
 * @example
 * getToastVoice('auth', 'login_success') // "Welcome back to the screening room."
 * getToastVoice('upload', 'success') // "Screenplay received. The read begins."
 */
export function getToastVoice(
  category: keyof ToastCategories,
  key: string,
  variables?: Record<string, string>
): string {
  const categoryMessages = voiceConfig.strings.toasts[category];
  
  if (!categoryMessages || !(key in categoryMessages)) {
    // Fallback to general category or return the key
    const generalMessages = voiceConfig.strings.toasts.general;
    if (generalMessages && key in generalMessages) {
      const message = generalMessages[key as keyof typeof generalMessages];
      return typeof message === 'string' ? message : getRandomOrSingle(message);
    }
    return key;
  }
  
  const messages = categoryMessages[key];
  let message = getRandomOrSingle(messages);
  
  // Interpolate variables if provided
  if (variables) {
    for (const [varKey, value] of Object.entries(variables)) {
      message = message.replace(`{${varKey}}`, value);
    }
  }
  
  return message;
}

/**
 * Get a system message (plain, no personality)
 * Use for errors, technical issues, and functional notifications.
 */
export function getSystemMessage(key: string): string {
  const errorStates = voiceConfig.strings.error_states;
  if (errorStates && key in errorStates) {
    return errorStates[key as keyof typeof errorStates];
  }
  return key;
}

/**
 * Get loading state message
 */
export function getLoadingVoice(state: string): string {
  const loadingStates = voiceConfig.strings.loading_states;
  if (loadingStates && state in loadingStates) {
    return loadingStates[state as keyof typeof loadingStates];
  }
  return state;
}

/**
 * Get error state message (system-level, plain)
 */
export function getErrorVoice(errorType: string): string {
  return getSystemMessage(errorType);
}

/**
 * Get budget description with voice
 */
export function getBudgetVoice(budgetBand: string): string {
  const descriptions = voiceConfig.strings.budget_descriptions;
  if (descriptions && budgetBand in descriptions) {
    return descriptions[budgetBand as keyof typeof descriptions];
  }
  return descriptions?.unknown || budgetBand;
}

/**
 * Get IP callout with voice
 */
export function getIPVoice(ipType: string): string {
  const template = voiceConfig.strings.ip_callout;
  return template.replace('{type}', ipType || 'existing');
}

/**
 * Get preview string
 */
export function getVoiceString(key: string, section: 'preview' | 'budget_descriptions' | 'loading_states' | 'error_states' = 'preview'): string {
  const sectionStrings = voiceConfig.strings[section];
  if (sectionStrings && typeof sectionStrings === 'object' && key in sectionStrings) {
    return sectionStrings[key as keyof typeof sectionStrings] as string;
  }
  return key;
}

/**
 * Template interpolation helper
 */
export function interpolate(template: string, variables: Record<string, string>): string {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(`{${key}}`, value);
  }
  return result;
}

/**
 * Get the current voice variant
 */
export function getVoiceVariant(): string {
  return voiceConfig.voice_variant;
}

// Legacy VoiceManager class for backward compatibility
class VoiceManager {
  getString(key: string, section: 'preview' | 'budget_descriptions' | 'loading_states' | 'error_states' = 'preview'): string {
    return getVoiceString(key, section);
  }

  getBudgetDescription(budgetBand: string): string {
    return getBudgetVoice(budgetBand);
  }

  getIPCallout(ipType: string): string {
    return getIPVoice(ipType);
  }

  getLoadingState(state: string): string {
    return getLoadingVoice(state);
  }

  getErrorState(errorType: string): string {
    return getErrorVoice(errorType);
  }

  getVariant(): string {
    return getVoiceVariant();
  }

  interpolate(template: string, variables: Record<string, string>): string {
    return interpolate(template, variables);
  }

  // No-op for backward compatibility
  async loadConfig(): Promise<void> {
    return Promise.resolve();
  }
}

// Singleton instance for backward compatibility
export const voiceManager = new VoiceManager();
